import {
  Droplets,
  ChevronDown,
  TrendingDown,
  TrendingUp,
  Info,
} from "lucide-react";
import { motion } from "framer-motion";
import { useState, useEffect, useContext, useRef } from "react";
import { createPortal } from "react-dom";
import api from "../services/api"; // Adjust based on your file structure
import { DistrictContext } from "../utils/DistrictContext";
import { getSystemUsage } from "../services/index";
import { useTheme } from "../context/ThemeContext";
import InfoTooltip from "../components/ui/InfoTooltip";
import { Spin } from "antd";

export function SystemWaterUsage({ data, onOptionChange, globalRange }) {
  const { theme, colors } = useTheme();
  const { selectedDistrict } = useContext(DistrictContext);
  const [selectedDays, setSelectedDays] = useState("Past Month");
  const [loading, setLoading] = useState(true);
  const buttonRef = useRef(null);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (globalRange) {
      setSelectedDays(globalRange);
    }
  }, [globalRange]);
  useEffect(() => {
    if (showInfoPopup && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.top,
        left: rect.right + 10,
      });
    }
  }, [showInfoPopup]);

  // API Data States
  const [usageData, setUsageData] = useState({
    percentage: 0,
    flow: "0",
    flowPre: "0",
    percentageDiff: "0",
    isDecrease: true,
    thisRange: "",
    preRange: "",
  });
  const getLabels = (selection) => {
    switch (selection) {
      case "week":
        return { current: "This Week", previous: "Last Week" };
      case "month":
        return { current: "This Month", previous: "Last Month" };
      case "year":
        return { current: "This Year", previous: "Last Year" };
      case "Last Week":
        return { current: "Last 7 Days", previous: "Prev. 7 Days" };
      case "Last Month":
        return { current: "Last 30 Days", previous: "Prev. 30 Days" };
      case "Last Year":
        return { current: "Last 365 Days", previous: "Prev. 365 Days" };
      default:
        return { current: selection, previous: `Previous ${selection}` };
    }
  };
  const timeOptions = [
    { label: "D", value: "Past Day" },
    { label: "W", value: "Past Week" },
    { label: "1M", value: "Past Month" },
    { label: "3M", value: "Past 3 Months" },
    { label: "6M", value: "Past 6 Months" },
    { label: "Y", value: "Past Year" },
  ];

  const labels = getLabels(selectedDays);
  useEffect(() => {
    const fetchUsage = async () => {
      try {
        setLoading(true);
        const res = await getSystemUsage(selectedDays);
        const data = res?.data?.systemusage;

        if (data) {
          setUsageData({
            percentage: data.percentage || 0,
            flow: data.flow || "0",
            flowPre: data.flow_pre || "0",
            percentageDiff: data.percentage_diff || "0",
            isDecrease: data.percentage_arrow_down === true,
            thisRange: data.this_range || "",
            preRange: data.pre_range || "",
          });
        }
      } catch (err) {
        console.error("Failed to fetch system usage", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsage();
  }, [selectedDays, selectedDistrict]);

  // Map the percentage to the height of the water tank (CSS height %)
  const waterHeight = `${usageData.percentage}%`;

  return (
    <motion.div
      className="rounded-xl p-6 transition-colors duration-300 relative overflow-hidden h-full flex flex-col group"
      style={{
        backgroundColor: theme === "white" ? "#ffffff" : colors.glassEffect,
        // backgroundColor: colors.cardBg,
        backdropFilter: "blur(10px)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: theme === "white" ? colors.border : `${colors.primary}15`,
      }}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.7, delay: 0.15, type: "spring", bounce: 0.4 }}
      whileHover={{
        x: 10,
        transition: { type: "spring", stiffness: 300 },
      }}
    >
      {/* Animated top line on hover */}
      <div
        className="absolute top-0 left-0 h-0.5 w-0 group-hover:w-full transition-all duration-400 ease-out rounded-full"
        style={{ backgroundImage: colors.gradient }}
      />

      <div className="relative z-10">
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundImage: colors.gradient }}
            >
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <h3
              className="text-2xl font-bold tracking-tight"
              style={{ color: colors.text }}
            >
              Water Usage
            </h3>
            <InfoTooltip text="This metric is calculated from networked meters only"></InfoTooltip>
          </div>

          {/* Days selector dropdown */}
          {/* <div className="relative">
            <select
              value={selectedDays}
              onChange={(e) => setSelectedDays(e.target.value)}
              className="appearance-none rounded-lg px-4 py-2 pr-10 text-sm font-medium transition-all duration-200 focus:outline-none"
              style={{
                    background: colors.background,
                    borderColor: colors.border,
                    color: colors.text,
                  }}
            >
              <option value="week"  style={{
    backgroundColor: theme === "white" ? "#ffffff" : colors.background,
    color: colors.text,
  }}>
                Week
              </option>
              <option value="month"  style={{
    backgroundColor: theme === "white" ? "#ffffff" : colors.background,
    color: colors.text,
  }}>
                Month
              </option>
              <option value="year"   style={{
    backgroundColor: theme === "white" ? "#ffffff" : colors.background,
    color: colors.text,
  }}>
                Year
              </option>
              <option value="Last Week"  style={{
    backgroundColor: theme === "white" ? "#ffffff" : colors.background,
    color: colors.text,
  }}>
                Last 7 Days
              </option>
              <option value="Last Month"  style={{
    backgroundColor: theme === "white" ? "#ffffff" : colors.background,
    color: colors.text,
  }}>
                Last 30 Days
              </option>

              <option value="Last Year"  style={{
    backgroundColor: theme === "white" ? "#ffffff" : colors.background,
    color: colors.text,
  }}>
                Last 365 Days
              </option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: colors.textSecondary }} />
          </div> */}
          <div
            className="flex items-center p-1 rounded-lg border bg-opacity-20 shrink-0 cursor-pointer "
            style={{
              backgroundColor: colors.background,
              borderColor: colors.border,
            }}
          >
            {timeOptions.map((tp) => {
              const isActive = selectedDays === tp.value;
              return (
                <button
                  key={tp.value}
                  onClick={() => setSelectedDays(tp.value)}
                  className="px-2.5 py-1 rounded-md text-[10px] min-[1921px]:text-[12px] font-black transition-all duration-200 uppercase cursor-pointer"
                  style={{
                    backgroundColor: isActive ? colors.primary : "transparent",
                    color: isActive ? "#ffffff" : colors.textSecondary,
                    boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.3)" : "none",
                  }}
                >
                  {tp.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Centered icon with data below */}
        {loading ? (
          <div
            className="flex flex-col items-center justify-center"
            style={{
              height: "300px",
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center mb-6 mt-12">
              {/* Water tank animation */}
              <div
                className="relative w-28 h-40  rounded-lg border-2 overflow-hidden"
                style={{
                  backgroundColor: colors.background,
                  borderColor: `${colors.primary}40`,
                }}
              >
                {/* Water with wavy surface */}
                <div
                  className="absolute bottom-0 left-0 right-0"
                  style={{ height: "75%" }}
                >
                  <svg
                    className="absolute inset-0 w-full h-full"
                    preserveAspectRatio="none"
                  >
                    <defs>
                      <linearGradient
                        id="waterGradient"
                        x1="0%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                      >
                        <stop
                          offset="0%"
                          stopColor={colors.primaryDark || colors.primary}
                        />
                        <stop offset="100%" stopColor={colors.primary} />
                      </linearGradient>
                    </defs>

                    {/* Animated wavy water surface */}
                    <motion.path
                      d="M0,20 Q14,10 28,20 T56,20 T84,20 T112,20 L112,160 L0,160 Z"
                      fill="url(#waterGradient)"
                      animate={{
                        d: [
                          "M0,20 Q14,10 28,20 T56,20 T84,20 T112,20 L112,160 L0,160 Z",
                          "M0,20 Q14,30 28,20 T56,20 T84,20 T112,20 L112,160 L0,160 Z",
                          "M0,20 Q14,10 28,20 T56,20 T84,20 T112,20 L112,160 L0,160 Z",
                        ],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                  </svg>
                  {/* <motion.div
                className="text-white text-3xl font-bold drop-shadow-lg z-10"
                animate={{
                  scale: [1, 1.03, 1],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                {usageData.percentage}{" "}
              </motion.div> */}
                </div>

                {/* Continuously floating bubbles */}
                {[0, 1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 bg-white/50 rounded-full"
                    style={{ left: `${15 + i * 18}%`, bottom: "0%" }}
                    animate={{
                      y: [-160, 20],
                      opacity: [0, 0.8, 0.6, 0],
                      scale: [0.3, 1, 1.2, 0.5],
                    }}
                    transition={{
                      duration: 3 + i * 0.4,
                      repeat: Infinity,
                      delay: i * 0.5,
                      ease: "easeOut",
                    }}
                  />
                ))}
              </div>

              {/* Percentage text */}
              <div className="mt-6 flex flex-col gap-3 w-full">
                <div
                  className=" flex items-center justify-center gap-2 px-4 py-2 backdrop-blur-sm border  rounded-lg"
                  style={{
                    // backgroundColor: `${colors.primary}10`,
                    borderColor: `${colors.primary}20`,
                  }}
                >
                  <motion.div
                    className="text-2xl font-bold drop-shadow-lg z-10"
                    animate={{
                      scale: [1, 1.03, 1],
                    }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    style={{ color: colors.text }}
                  >
                    {usageData.percentage}{" "}
                  </motion.div>
                  <motion.div
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg  transition-colors"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 3.6 }}
                    //                   style={{
                    //   backgroundColor: `${colors.primary}10`,
                    //   border: `1px solid ${colors.primary}20`,
                    // }}
                  >
                    <motion.div
                      animate={{
                        y: [0, 4, 0],
                        rotate: [0, -8, 0, 8, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      {usageData.isDecrease ? (
                        <TrendingDown className="w-6 h-6 text-[#4CAF50] font-bold" />
                      ) : (
                        <TrendingUp className="w-6 h-6 text-[#ff6b00] font-bold" />
                      )}
                    </motion.div>
                  </motion.div>
                </div>
              </div>

              {/* Data display below icon */}
              <div
                className="pt-6 border-t  w-full mt-5"
                style={{ borderColor: colors.border }}
              >
                <div className="grid grid-cols-2 max-[1650px]:grid-cols-1 gap-4">
                  <motion.div
                    className="rounded-lg p-4 border transition-colors h-full"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: .3 }}
                    style={{
                      backgroundColor: `${colors.primary}10`,
                      borderColor: `${colors.primary}20`,
                    }}
                  >
                    <span
                      className=" text-xs block mb-1"
                      style={{ color: colors.textSecondary }}
                    >
                      {/* {selectedDays} */}
                      {labels.current}
                    </span>
                    <span
                      className="text-[14px] font-regular"
                      style={{ color: colors.text }}
                    >
                      {usageData.thisRange}
                    </span>
                    <div
                      className="text-2xl font-bold"
                      style={{ color: colors.text }}
                    >
                      {usageData.flow}
                    </div>
                    <span
                      className="text-[12px]"
                      style={{ color: colors.textSecondary }}
                    >
                      Gallons
                    </span>
                  </motion.div>

                  <motion.div
                    className="backdrop-blur-sm rounded-lg p-3 border transition-colors"
                    style={{
                      backgroundColor: `${colors.primary}10`,
                      borderColor: `${colors.primary}20`,
                      height: "100%",
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: .3 }}
                  >
                    <span
                      className="text-xs block mb-1"
                      style={{ color: colors.textSecondary }}
                    >
                      {labels.previous}
                    </span>
                    <span
                      className=" text-[14px] font-regular"
                      style={{ color: colors.text }}
                    >
                      {usageData.preRange}
                    </span>
                    <div
                      className="text-2xl font-bold"
                      style={{ color: colors.text }}
                    >
                      {usageData.flowPre}
                    </div>
                    <span
                      className="text-[12px]"
                      style={{ color: colors.textSecondary }}
                    >
                      Gallons
                    </span>
                  </motion.div>
                </div>

                <svg width="0" height="0" style={{ position: "absolute" }}>
                  <defs>
                    <linearGradient
                      id="orangeGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#ff6b00" />
                      <stop offset="100%" stopColor="#ff9500" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
          </>
        )}
        {/* <div className="pt-4 border-t border-[#2a2520]">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[#BDB9B5] text-xs">Date Range Current:</span>
            <span className="text-white text-xs font-medium">
              {usageData.thisRange}
            </span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[#BDB9B5] text-xs">Date Range Previous:</span>
            <span className="text-white text-xs font-medium">
              {usageData.preRange}
            </span>
          </div>
          <span className="text-[#BDB9B5] text-xs">
            *This metric is calculated from networked meters only
          </span>
        </div> */}
      </div>
    </motion.div>
  );
}
